import { useId, useState } from 'react';
import { ExportOutlined, PaperClipOutlined, UploadOutlined } from '@ant-design/icons';
import { Segmented, Upload } from 'antd';

import { Button, Dialog, Field, Input, Select, Textarea } from '../../../components/ui';
import { useT } from '../../../i18n';
import { dungLuongVN } from '../../../lib/dinh-dang';
import { loiTheoTruong } from '../../../lib/errors';
import { useToast } from '../../../lib/toast';
import { LoiTrongHop } from '../../tenant/components/phan-form';
import { DANH_MUC, KHOA_LOI_TAI_LIEU } from '../constants';
import { useLoiBaoCao, useLuuTaiLieu, useMoTaiLieu } from '../hooks/useBaoCao';
import {
  DUOI_NHAN,
  banNhapTaiLieuMoi,
  coTep,
  kiemTaiLieu,
  thanSuaTaiLieu,
  thanTaoTaiLieu,
  tuTaiLieu,
} from '../nghiep-vu/payload';
import type { BanNhapTaiLieu, NguonNoiDung } from '../nghiep-vu/payload';
import { CATEGORIES, DOC_TYPES } from '../types';
import type { Category, DocType, DocumentItem, Folder } from '../types';
import { ChonIcon, ChonMauNhan } from './ChonEnum';
import { ChonThuMuc } from './ChonThuMuc';

/**
 * Thêm hoặc sửa một tài liệu báo cáo. Nội dung lấy từ **tệp tải lên** (multipart `file` + `data`) hoặc
 * **liên kết ngoài** (JSON có `url`).
 *
 * Tài liệu có tệp hiện tên tệp và nút mở (gọi `open` lấy URL ký), **không** hiện ô URL: địa chỉ `open` cố
 * định mà gửi ngược lên là backend bỏ tệp đang gắn. Bản nháp khởi tạo một lần lúc mở hộp (phía gọi gắn
 * `key` theo tài liệu), không đồng bộ bằng effect.
 */
export function HopTaiLieu({
  goc,
  thuMucMacDinh,
  dsThuMuc,
  nguon,
  onClose,
}: {
  /** Vắng là thêm mới. */
  goc?: DocumentItem;
  /** Thư mục đang xem lúc bấm "Thêm" — tài liệu mới cất vào đó. */
  thuMucMacDinh: string | null;
  dsThuMuc: readonly Folder[];
  /** Mã nguồn đang xem; tài liệu mới ghi vào nguồn này. */
  nguon: string | undefined;
  onClose: () => void;
}) {
  const { t, locale } = useT();
  const toast = useToast();
  const dichLoi = useLoiBaoCao();
  const formId = useId();
  const luu = useLuuTaiLieu(nguon);
  const mo = useMoTaiLieu();

  const [b, setB] = useState<BanNhapTaiLieu>(() => (goc ? tuTaiLieu(goc) : banNhapTaiLieuMoi(thuMucMacDinh)));
  const [tep, setTep] = useState<File | null>(null);
  const [daGui, setDaGui] = useState(false);

  const doi = (p: Partial<BanNhapTaiLieu>) => {
    setB((cu) => ({ ...cu, ...p }));
    luu.reset();
  };

  const loiMay = kiemTaiLieu(b, tep, goc);
  // Lỗi tệp hiện ngay khi chọn; lỗi bỏ trống chỉ hiện sau lần bấm gửi đầu.
  const loi = daGui ? loiMay : { file: loiMay.file === 'chuaChonTep' ? undefined : loiMay.file };
  const truongServer = luu.error ? loiTheoTruong(luu.error) : {};
  const loiChung = luu.error && Object.keys(truongServer).length === 0 ? dichLoi(luu.error) : null;
  const loiO = (truong: 'title' | 'url' | 'file'): string | undefined => {
    const ma = loi[truong];
    return ma ? t(KHOA_LOI_TAI_LIEU[ma]) : truongServer[`/${truong}`];
  };

  const gui = () => {
    setDaGui(true);
    if (Object.keys(loiMay).length > 0 || luu.isPending) return;
    const tepGui = b.nguonNoiDung === 'tep' ? tep : null;
    const du = goc ? thanSuaTaiLieu(b, goc, tepGui !== null) : thanTaoTaiLieu(b);
    if (goc && tepGui === null && Object.keys(du).length === 0) {
      onClose();
      return;
    }
    luu.mutate(
      { ...(goc ? { id: goc.id } : {}), du, tep: tepGui },
      {
        onSuccess: (moi) => {
          toast.success(t(goc ? 'baoCaoDoanhNghiep.taiLieu.daLuu' : 'baoCaoDoanhNghiep.taiLieu.daThem', { ten: moi.title }));
          onClose();
        },
      },
    );
  };

  const tepCu = goc && coTep(goc) ? goc : undefined;

  return (
    <Dialog
      open
      onClose={onClose}
      title={t(goc ? 'baoCaoDoanhNghiep.taiLieu.suaTieuDe' : 'baoCaoDoanhNghiep.taiLieu.themTieuDe')}
      width={720}
      destroyOnHidden
      footer={
        <div className="space-y-3">
          {loiChung && <LoiTrongHop>{loiChung}</LoiTrongHop>}
          <div className="flex flex-wrap justify-end gap-3">
            <Button variant="outline" size="md" onClick={onClose}>
              {t('chung.huy')}
            </Button>
            <Button variant="primary" size="md" htmlType="submit" form={formId} loading={luu.isPending}>
              {luu.isPending
                ? t('chung.dangLuu')
                : t(goc ? 'baoCaoDoanhNghiep.taiLieu.luuThayDoi' : 'baoCaoDoanhNghiep.taiLieu.them')}
            </Button>
          </div>
        </div>
      }
    >
      <form
        id={formId}
        noValidate
        className="grid grid-cols-1 gap-4 pt-2 sm:grid-cols-2"
        onSubmit={(e) => {
          e.preventDefault();
          gui();
        }}
      >
        <Field label={t('baoCaoDoanhNghiep.taiLieu.cotTieuDe')} required error={loiO('title')} className="sm:col-span-2">
          {(p) => <Input {...p} value={b.title} maxLength={300} onChange={(e) => doi({ title: e.target.value })} />}
        </Field>

        <div className="space-y-3 sm:col-span-2">
          <Segmented<NguonNoiDung>
            aria-label={t('baoCaoDoanhNghiep.taiLieu.noiDung')}
            value={b.nguonNoiDung}
            onChange={(v) => doi({ nguonNoiDung: v })}
            options={[
              { value: 'tep', label: t('baoCaoDoanhNghiep.taiLieu.tepTaiLen') },
              { value: 'lien-ket', label: t('baoCaoDoanhNghiep.taiLieu.lienKetNgoai') },
            ]}
          />

          {b.nguonNoiDung === 'tep' ? (
            <Field label={t('baoCaoDoanhNghiep.taiLieu.tep')} required error={loiO('file')} hint={t('baoCaoDoanhNghiep.taiLieu.tepGoiY')}>
              {(p) => (
                <div className="space-y-2">
                  {(tep || tepCu) && (
                    <div className="flex min-w-0 flex-wrap items-center gap-2 rounded border border-default bg-subtle px-3 py-2">
                      <PaperClipOutlined className="shrink-0 text-content-muted" aria-hidden />
                      <span className="min-w-0 flex-1 truncate text-sm text-content-primary" title={tep?.name ?? tepCu?.fileName}>
                        {tep?.name ?? tepCu?.fileName}
                      </span>
                      <span className="shrink-0 text-xs tabular-nums text-content-muted">
                        {dungLuongVN(tep?.size ?? tepCu?.fileSizeBytes ?? 0, locale)}
                      </span>
                      {tep && tepCu && <span className="shrink-0 text-xs text-content-muted">{t('baoCaoDoanhNghiep.taiLieu.seThayTep')}</span>}
                      {!tep && tepCu && (
                        <Button
                          variant="ghost"
                          size="sm"
                          icon={<ExportOutlined />}
                          loading={mo.isPending}
                          onClick={() => mo.mutate(tepCu.id, { onError: (e) => toast.error(dichLoi(e)) })}
                        >
                          {t('baoCaoDoanhNghiep.taiLieu.moTep')}
                        </Button>
                      )}
                    </div>
                  )}
                  <Upload
                    accept={DUOI_NHAN.join(',')}
                    showUploadList={false}
                    maxCount={1}
                    beforeUpload={(f) => {
                      setTep(f);
                      luu.reset();
                      return Upload.LIST_IGNORE;
                    }}
                  >
                    <Button
                      variant="outline"
                      size="md"
                      icon={<UploadOutlined />}
                      id={p.id}
                      aria-invalid={p['aria-invalid']}
                      aria-describedby={p['aria-describedby']}
                    >
                      {t(tep || tepCu ? 'baoCaoDoanhNghiep.taiLieu.chonTepKhac' : 'baoCaoDoanhNghiep.taiLieu.chonTep')}
                    </Button>
                  </Upload>
                </div>
              )}
            </Field>
          ) : (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <Field
                label={t('baoCaoDoanhNghiep.taiLieu.url')}
                required
                error={loiO('url')}
                hint={t(tepCu ? 'baoCaoDoanhNghiep.taiLieu.ghiChuUrlBoTep' : 'baoCaoDoanhNghiep.taiLieu.ghiChuUrl')}
                className="sm:col-span-2"
              >
                {(p) => (
                  <Input
                    {...p}
                    type="url"
                    inputMode="url"
                    value={b.url}
                    placeholder={t('baoCaoDoanhNghiep.taiLieu.urlGoiY')}
                    onChange={(e) => doi({ url: e.target.value })}
                  />
                )}
              </Field>
              <Field label={t('baoCaoDoanhNghiep.taiLieu.cotLoai')}>
                {(p) => (
                  <Select<DocType>
                    {...p}
                    className="w-full"
                    value={b.type}
                    onChange={(v) => doi({ type: v })}
                    options={DOC_TYPES.map((x) => ({ value: x, label: t(`baoCaoDoanhNghiep.loaiTep.${x}`) }))}
                  />
                )}
              </Field>
            </div>
          )}
        </div>

        {/* Danh mục và thư mục đứng cạnh nhau: danh mục quyết định phân hệ nào của app hiện tài liệu, thư
            mục chỉ là chỗ cất. Tách xa thì người nhập tưởng phải chọn một trong hai. */}
        <Field label={t('baoCaoDoanhNghiep.taiLieu.cotDanhMuc')} hint={t('baoCaoDoanhNghiep.taiLieu.danhMucGoiY')}>
          {(p) => (
            <Select<Category>
              {...p}
              className="w-full"
              value={b.category}
              onChange={(v) => doi({ category: v })}
              options={CATEGORIES.map((x) => ({ value: x, label: t(DANH_MUC[x].khoa) }))}
            />
          )}
        </Field>
        <Field label={t('baoCaoDoanhNghiep.thuMuc.tieuDe')}>
          {(p) => (
            <ChonThuMuc
              {...p}
              danhSach={dsThuMuc}
              giaTri={b.folderId}
              onChon={(id) => doi({ folderId: id })}
              nhanGoc={t('baoCaoDoanhNghiep.thuMuc.chuaPhanLoai')}
            />
          )}
        </Field>
        <Field label={t('baoCaoDoanhNghiep.chiSo.bieuTuong')}>
          {(p) => <ChonIcon {...p} value={b.iconName} onChange={(v) => doi({ iconName: v })} />}
        </Field>
        <Field label={t('baoCaoDoanhNghiep.chiSo.mauNhan')}>
          {(p) => <ChonMauNhan {...p} value={b.accentName} onChange={(v) => doi({ accentName: v })} />}
        </Field>
        <Field label={t('baoCaoDoanhNghiep.taiLieu.tomTat')} className="sm:col-span-2">
          {(p) => (
            <Textarea {...p} rows={2} maxLength={1000} value={b.summary} onChange={(e) => doi({ summary: e.target.value })} />
          )}
        </Field>
        <Field label={t('baoCaoDoanhNghiep.taiLieu.meta')} hint={t('baoCaoDoanhNghiep.taiLieu.ghiChuMeta')} className="sm:col-span-2">
          {(p) => <Input {...p} value={b.meta} maxLength={120} onChange={(e) => doi({ meta: e.target.value })} />}
        </Field>
      </form>
    </Dialog>
  );
}
